import { Component, type ReactNode, type ErrorInfo } from "react";
import { Button } from "@/components/ui/button";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import { Copy, Check } from "@/components/ui/icons";
import { copyToClipboard } from "@/lib/clipboard";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
  incidentId: string | null;
  copied: boolean;
}

function generateIncidentId(): string {
  try {
    return crypto.randomUUID().slice(0, 8);
  } catch {
    return Math.random().toString(16).slice(2, 10);
  }
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    componentStack: null,
    incidentId: null,
    copied: false,
  };

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error,
      incidentId: generateIncidentId(),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", {
      incidentId: this.state.incidentId,
      error,
      componentStack: info.componentStack,
    });
    this.setState({ componentStack: info.componentStack ?? null });
    const capture = (
      window as unknown as {
        __sentryCaptureException?: (e: unknown, hint?: unknown) => void;
      }
    ).__sentryCaptureException;
    capture?.(error, {
      tags: { incidentId: this.state.incidentId },
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      componentStack: null,
      incidentId: null,
      copied: false,
    });
  };

  handleCopyId = async () => {
    if (!this.state.incidentId) return;
    try {
      await copyToClipboard(this.state.incidentId);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1500);
    } catch {
      // Clipboard may be unavailable — the id stays visible on screen.
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="flex h-screen items-center justify-center bg-background">
            <div className="w-[420px] space-y-4 text-center">
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Something went wrong
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  The app hit an unexpected error. You can try again or reload
                  the page.
                </p>
              </div>

              {this.state.incidentId && (
                <div className="inline-flex items-center gap-2 rounded-md bg-foreground/[0.03] px-2 py-1 text-[11px] text-muted-foreground">
                  <span>Reference:</span>
                  <span className="font-mono text-foreground">
                    {this.state.incidentId}
                  </span>
                  <ToolbarIconButton
                    onClick={this.handleCopyId}
                    aria-label="Copy reference id"
                    className="h-6 w-6"
                  >
                    {this.state.copied ? (
                      <Check size={12} className="text-success" />
                    ) : (
                      <Copy size={12} />
                    )}
                  </ToolbarIconButton>
                </div>
              )}

              <div className="flex items-center justify-center gap-2">
                <Button onClick={this.handleReset}>Try again</Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  Reload page
                </Button>
              </div>

              <details className="text-left mt-4">
                <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                  Technical details
                </summary>
                <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-foreground/[0.03] p-2 text-[11px] text-muted-foreground whitespace-pre-wrap font-mono">
                  {this.state.error?.name}: {this.state.error?.message}
                  {this.state.componentStack
                    ? `\n${this.state.componentStack}`
                    : ""}
                </pre>
              </details>
            </div>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
